import { useQuery } from '@tanstack/react-query'

import api from '@/admin/lib/api'

/**
 * Communications activity: what was sent about a record, and what was not.
 *
 * The backend normalises every source (email, SMS, WhatsApp, voice) onto one row
 * shape, so nothing here branches on where a row came from. Cache policy is
 * registered by key prefix in query-defaults.ts - do not set staleTime here.
 */

/** Normalised across every source. Mirrors `activity.query.Status`. */
export type ActivityStatus =
  | 'queued'
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'clicked'
  | 'bounced'
  | 'failed'
  | 'skipped'

export type ActivitySource = 'message' | 'voice'
export type ActivityChannel = 'email' | 'sms' | 'whatsapp' | 'voice' | 'mail'

export interface ActivityRow {
  source: ActivitySource
  uuid: string
  occurred_at: string
  channel: ActivityChannel
  direction: 'inbound' | 'outbound'
  status: ActivityStatus
  /** The store's own value, for support. Never rendered as a label. */
  status_raw: string
  counterparty: string
  counterparty_name: string
  summary: string
  target_type: string
  target_uuid: string | null
  organisation_id: number | null
  /**
   * Who the message went to, decided by whether the recipient is a member of
   * the business (WPS-146). NOT by email domain - a tenant's own staff file
   * claims as customers, so the same address appears on both sides.
   */
  audience: 'internal' | 'external' 
  /** Machine code. Render `skip_reason_label` instead. */
  skip_reason: string
  skip_reason_label: string
  cause: { workflow_action_log_id?: number }
}

export interface ActivityPage {
  results: ActivityRow[]
  next_cursor: string | null
  /** Total matching rows. Only present in page mode, and null when the backend
   * cannot count exactly (voice filtered by status). */
  count?: number | null
}

export interface ActivitySummary {
  total: number
  by_status: Partial<Record<ActivityStatus, number>>
  by_channel: Partial<Record<ActivityChannel, number>>
  by_skip_reason: Record<string, number>
  /** The kind filter's options. Server-supplied so no enum is duplicated here,
   * and not date-scoped so the filter survives an empty period. */
  available_kinds: string[]
}

export interface ActivityQueryParams {
  source?: ActivitySource[]
  channel?: ActivityChannel[]
  status?: ActivityStatus[]
  skip_reason?: string[]
  kind?: string[]
  date_from?: string
  date_to?: string
  search?: string
  page_size?: number
  /** 1-indexed. Switches the API from cursor mode to numbered pages. */
  page?: number
  cursor?: string
}

/** The API takes comma-separated lists; empty values are omitted entirely. */
function toParams(params: ActivityQueryParams): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue
    if (Array.isArray(value)) {
      if (value.length > 0) out[key] = value.join(',')
    } else if (value !== '') {
      out[key] = String(value)
    }
  }
  return out
}

const EMPTY_PAGE: ActivityPage = { results: [], next_cursor: null, count: 0 }

/** Tenant-wide activity stream for the Settings console. */
export function useCommunicationsActivity(params: ActivityQueryParams = {}) {
  const query = toParams(params)
  return useQuery<ActivityPage>({
    queryKey: ['communications-activity', query],
    queryFn: async () => {
      const res = await api.get('/admin/communications/activity/', { params: query })
      return res.data ?? EMPTY_PAGE
    },
  })
}

/**
 * Every communication about one record.
 *
 * Disabled until both parts of the target are known, so a detail page that has
 * not resolved its record yet does not fire a request for `undefined`.
 */
export function useRecordCommunicationsActivity(
  targetType: string | undefined,
  targetUuid: string | undefined,
  params: ActivityQueryParams = {},
) {
  const query = toParams(params)
  return useQuery<ActivityPage>({
    queryKey: ['communications-activity', 'record', targetType, targetUuid, query],
    enabled: Boolean(targetType && targetUuid),
    queryFn: async () => {
      const res = await api.get(
        `/admin/communications/records/${targetType}/${targetUuid}/activity/`,
        { params: query },
      )
      return res.data ?? EMPTY_PAGE
    },
  })
}

/** Deliverability rollup behind the console's stat tiles. */
export function useCommunicationsActivitySummary(
  params: Pick<ActivityQueryParams, 'date_from' | 'date_to'> = {},
) {
  const query = toParams(params)
  return useQuery<ActivitySummary>({
    queryKey: ['communications-activity-summary', query],
    queryFn: async () => {
      const res = await api.get('/admin/communications/activity/summary/', {
        params: query,
      })
      return (
        res.data ?? {
          total: 0,
          by_status: {},
          by_channel: {},
          by_skip_reason: {},
          available_kinds: [],
        }
      )
    },
  })
}

export interface ActivityDetail {
  source: ActivitySource
  uuid: string
  occurred_at: string
  channel: ActivityChannel
  // message
  subject?: string
  body_html?: string
  body_text?: string
  recipient?: string
  recipient_name?: string
  recipient_kind?: string
  status?: ActivityStatus
  status_raw?: string
  provider_status?: string
  /** ESP id. Empty means the send captured none, so no tracking event
   * can ever match this row. */
  provider_message_id?: string
  error_code?: string
  skip_reason?: string
  skip_reason_label?: string
  skip_detail?: Record<string, unknown>
  /** Referenced by the template but unsupplied - why a body renders blank. */
  missing_variables?: string[]
  attachments?: { pdf_attached: boolean; output_modes: string[] }
  template_slug?: string
  template_version?: string | number
  // voice
  direction?: string
  duration_ms?: number | null
  outcome?: string
  sentiment?: string
  summary?: string
  transcript?: string
  recording_url?: string
  needs_human_callback?: boolean
  // shared
  target_type?: string
  target_uuid?: string | null
  cause?: { workflow_action_log_id?: number }
}

/**
 * One row in full. Fetched only once its row is expanded, so opening the console
 * costs one request rather than one per row.
 */
export function useCommunicationActivityDetail(
  source: ActivitySource | undefined,
  rowUuid: string | undefined,
) {
  return useQuery<ActivityDetail>({
    queryKey: ['communication-activity', source, rowUuid],
    enabled: Boolean(source && rowUuid),
    queryFn: async () => {
      const res = await api.get(`/admin/communications/activity/${source}/${rowUuid}/`)
      return res.data
    },
  })
}
